import { useState, useEffect, useMemo } from 'react';
import { useModalProtection } from '@/hooks/useModalProtection';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, addDays, isWeekend, differenceInDays, addBusinessDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarIcon, AlertTriangle, User, CheckCircle2, Loader2, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ChantierBrouillon {
  id: string;
  nom: string;
  date_debut_prevue: string | null;
  date_fin_estimee: string | null;
  client_nom: string | null;
  entreprise_id: string;
}

interface Conflit {
  membreId: string;
  membreNom: string;
  chantierNom: string;
  dateDebut: string;
  dateFin: string;
  affectationId: string;
}

interface ModalDemarrageBrouillonProps {
  isOpen: boolean;
  onClose: () => void;
  chantier: ChantierBrouillon | null;
  onDemarrer: (chantierId: string) => void;
}

/**
 * Modal de démarrage d'un brouillon avec :
 * - Confirmation/modification des dates de début et de fin
 * - Vérification des conflits d'affectation des ouvriers
 * - Options de résolution si conflit détecté
 */
export function ModalDemarrageBrouillon({
  isOpen,
  onClose,
  chantier,
  onDemarrer,
}: ModalDemarrageBrouillonProps) {
  const { toast } = useToast();
  useModalProtection(isOpen);

  // Dates modifiables
  const [dateDebut, setDateDebut] = useState<Date | undefined>(undefined);
  const [dateFin, setDateFin] = useState<Date | undefined>(undefined);

  // États de chargement
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Conflits détectés
  const [conflits, setConflits] = useState<Conflit[]>([]);
  const [showConflitModal, setShowConflitModal] = useState(false);

  // Initialiser les dates quand le modal s'ouvre
  useEffect(() => {
    if (isOpen && chantier) {
      if (chantier.date_debut_prevue) {
        setDateDebut(new Date(chantier.date_debut_prevue));
      } else {
        // Par défaut: aujourd'hui
        setDateDebut(new Date());
      }

      if (chantier.date_fin_estimee) {
        setDateFin(new Date(chantier.date_fin_estimee));
      } else {
        // Par défaut: dans 30 jours
        setDateFin(addDays(new Date(), 30));
      }

      setConflits([]);
      setShowConflitModal(false);
    }
  }, [isOpen, chantier]);

  // Calculer la durée en jours ouvrés
  const dureeJoursOuvres = useMemo(() => {
    if (!dateDebut || !dateFin) return 0;
    let count = 0;
    let current = new Date(dateDebut);
    while (current <= dateFin) {
      if (!isWeekend(current)) count++;
      current = addDays(current, 1);
    }
    return count;
  }, [dateDebut, dateFin]);

  if (!chantier) return null;

  /**
   * Vérifie les conflits d'affectation pour les membres affectés au brouillon
   */
  const verifierConflits = async (): Promise<Conflit[]> => {
    if (!dateDebut || !dateFin) return [];

    const dateDebutStr = format(dateDebut, 'yyyy-MM-dd');
    const dateFinStr = format(dateFin, 'yyyy-MM-dd');

    // 1. Récupérer les membres affectés à ce brouillon
    const { data: affectationsBrouillon, error: errAffBrouillon } = await supabase
      .from('affectations_chantiers')
      .select(`
        membre_equipe_id,
        membres_equipe!inner(id, prenom, nom)
      `)
      .eq('chantier_id', chantier.id);

    if (errAffBrouillon || !affectationsBrouillon || affectationsBrouillon.length === 0) {
      if (import.meta.env.DEV) console.log('[ModalDemarrageBrouillon] Pas d\'affectations sur ce brouillon');
      return [];
    }

    const membresIds = affectationsBrouillon.map(a => a.membre_equipe_id);
    if (import.meta.env.DEV) console.log('[ModalDemarrageBrouillon] Vérification conflits pour', membresIds.length, 'membres');

    // 2. Vérifier si ces membres sont affectés ailleurs pendant la période
    const { data: affectationsConflictuelles, error: errConflits } = await supabase
      .from('affectations_chantiers')
      .select(`
        id,
        membre_equipe_id,
        date_debut,
        date_fin,
        chantiers!inner(id, nom_chantier, etat_chantier)
      `)
      .in('membre_equipe_id', membresIds)
      .neq('chantier_id', chantier.id)
      .lte('date_debut', dateFinStr)
      .gte('date_fin', dateDebutStr);

    if (errConflits) {
      console.error('[ModalDemarrageBrouillon] Erreur vérification conflits:', errConflits);
      return [];
    }

    // Filtrer pour ne garder que les conflits avec des chantiers actifs (pas les brouillons, terminés, etc.)
    const conflitsActifs = (affectationsConflictuelles || []).filter(aff => {
      const etat = (aff.chantiers as any)?.etat_chantier;
      return etat === 'en_cours' || etat === 'planifie';
    });

    if (import.meta.env.DEV) console.log('[ModalDemarrageBrouillon] Conflits actifs trouvés:', conflitsActifs.length);

    // Construire la liste des conflits
    const conflitsDetectes: Conflit[] = conflitsActifs.map(aff => {
      const membreInfo = affectationsBrouillon.find(a => a.membre_equipe_id === aff.membre_equipe_id);
      const membre = membreInfo?.membres_equipe as any;
      const chantierInfo = aff.chantiers as any;

      return {
        membreId: aff.membre_equipe_id,
        membreNom: membre ? `${membre.prenom} ${membre.nom}` : 'Inconnu',
        chantierNom: chantierInfo?.nom_chantier || 'Chantier inconnu',
        dateDebut: aff.date_debut,
        dateFin: aff.date_fin,
        affectationId: aff.id,
      };
    });

    return conflitsDetectes;
  };

  /**
   * Valide les dates et vérifie les conflits
   */
  const handleValider = async () => {
    if (!dateDebut || !dateFin) {
      toast({
        title: "Dates requises",
        description: "Veuillez définir les dates de début et de fin.",
        variant: "destructive",
      });
      return;
    }

    if (dateFin < dateDebut) {
      toast({
        title: "Dates invalides",
        description: "La date de fin doit être après la date de début.",
        variant: "destructive",
      });
      return;
    }

    setCheckingConflicts(true);

    try {
      const conflitsDetectes = await verifierConflits();

      if (conflitsDetectes.length > 0) {
        setConflits(conflitsDetectes);
        setShowConflitModal(true);
      } else {
        // Pas de conflit → démarrer directement
        await demarrerChantier();
      }
    } catch (error) {
      console.error('[ModalDemarrageBrouillon] Erreur:', error);
      toast({
        title: "Erreur",
        description: "Impossible de vérifier les conflits d'affectation.",
        variant: "destructive",
      });
    } finally {
      setCheckingConflicts(false);
    }
  };

  /**
   * Démarre le chantier (met à jour les dates et passe en "en_cours")
   */
  const demarrerChantier = async () => {
    if (!dateDebut || !dateFin) return;

    setProcessing(true);

    try {
      // Mettre à jour les dates et le statut
      const { error } = await supabase
        .from('chantiers')
        .update({
          date_debut_prevue: format(dateDebut, 'yyyy-MM-dd'),
          date_fin_estimee: format(dateFin, 'yyyy-MM-dd'),
          etat_chantier: 'en_cours',
        })
        .eq('id', chantier.id);

      if (error) throw error;

      toast({
        title: "Chantier démarré",
        description: `"${chantier.nom}" est maintenant en cours.`,
      });

      onDemarrer(chantier.id);
      onClose();
    } catch (error) {
      console.error('[ModalDemarrageBrouillon] Erreur démarrage:', error);
      toast({
        title: "Erreur",
        description: "Impossible de démarrer le chantier.",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  /**
   * Option 1 : Faire une rotation (libérer les membres des autres chantiers)
   */
  const handleRotation = async () => {
    if (!dateDebut || !dateFin) return;

    setProcessing(true);

    try {
      const dateDebutStr = format(dateDebut, 'yyyy-MM-dd');
      const nouvelleDateFin = addDays(dateDebut, -1);
      const nouvelleDateFinStr = format(nouvelleDateFin, 'yyyy-MM-dd');

      // Terminer les affectations conflictuelles
      for (const conflit of conflits) {
        // Si la nouvelle date de fin est avant la date de début, supprimer l'affectation
        if (nouvelleDateFin < new Date(conflit.dateDebut)) {
          await supabase
            .from('affectations_chantiers')
            .delete()
            .eq('id', conflit.affectationId);
        } else {
          // Sinon, raccourcir l'affectation
          await supabase
            .from('affectations_chantiers')
            .update({ date_fin: nouvelleDateFinStr })
            .eq('id', conflit.affectationId);
        }
      }

      // Démarrer le chantier
      await demarrerChantier();
      setShowConflitModal(false);
    } catch (error) {
      console.error('[ModalDemarrageBrouillon] Erreur rotation:', error);
      toast({
        title: "Erreur",
        description: "Impossible de faire la rotation d'équipe.",
        variant: "destructive",
      });
      setProcessing(false);
    }
  };

  /**
   * Option 2 : Démarrer malgré les conflits (double affectation)
   */
  const handleDemarrerMalgre = async () => {
    await demarrerChantier();
    setShowConflitModal(false);
  };

  /**
   * Option 3 : Annuler et modifier les dates
   */
  const handleAnnulerConflit = () => {
    setShowConflitModal(false);
  };

  // Date minimum: aujourd'hui
  const minDate = new Date();
  minDate.setHours(0, 0, 0, 0);

  return (
    <>
      {/* Modal principal de confirmation des dates */}
      <Dialog open={isOpen && !showConflitModal} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <span className="text-2xl">📋</span>
              Démarrer le brouillon
            </DialogTitle>
            <DialogDescription className="text-left pt-2">
              Confirmez les dates de démarrage pour
              <span className="font-semibold text-foreground"> "{chantier.nom}"</span>
              {chantier.client_nom && (
                <span className="text-muted-foreground"> ({chantier.client_nom})</span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Date de début */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Date de début</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !dateDebut && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateDebut ? (
                      format(dateDebut, 'dd MMMM yyyy', { locale: fr })
                    ) : (
                      "Sélectionner une date"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateDebut}
                    onSelect={setDateDebut}
                    disabled={(date) => date < minDate}
                    initialFocus
                    locale={fr}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Date de fin */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Date de fin estimée</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !dateFin && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateFin ? (
                      format(dateFin, 'dd MMMM yyyy', { locale: fr })
                    ) : (
                      "Sélectionner une date"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateFin}
                    onSelect={setDateFin}
                    disabled={(date) => date < (dateDebut || minDate)}
                    initialFocus
                    locale={fr}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Résumé durée */}
            {dateDebut && dateFin && (
              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <ArrowRight className="h-4 w-4" />
                  <span>
                    Durée estimée : <span className="font-semibold text-foreground">{dureeJoursOuvres} jours travaillés</span>
                  </span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={onClose} disabled={processing || checkingConflicts}>
              Annuler
            </Button>
            <Button onClick={handleValider} disabled={processing || checkingConflicts || !dateDebut || !dateFin}>
              {checkingConflicts ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Vérification...
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Démarrer le chantier
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de résolution des conflits */}
      <AlertDialog open={showConflitModal} onOpenChange={setShowConflitModal}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-orange-500">
              <AlertTriangle className="h-5 w-5" />
              Conflit d'affectation détecté
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  <strong>{conflits.length}</strong> membre(s) sont déjà affectés sur un autre chantier pendant cette période.
                </p>
                <p className="text-sm text-muted-foreground">
                  Choisissez comment résoudre ce conflit pour éviter le double comptage des coûts.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* Liste des conflits */}
          <div className="space-y-2 my-4 max-h-40 overflow-y-auto">
            {conflits.map((conflit, i) => (
              <div key={i} className="flex items-center gap-2 p-2 bg-orange-500/10 rounded text-sm">
                <User className="h-4 w-4 text-orange-500 flex-shrink-0" />
                <span className="font-medium">{conflit.membreNom}</span>
                <span className="text-muted-foreground">
                  → {conflit.chantierNom} ({format(new Date(conflit.dateDebut), 'dd/MM', { locale: fr })} - {format(new Date(conflit.dateFin), 'dd/MM', { locale: fr })})
                </span>
              </div>
            ))}
          </div>

          {/* Options de résolution */}
          <div className="space-y-3">
            {/* Option 1 : Rotation */}
            <Button
              variant="default"
              className="w-full justify-start h-auto py-3 px-4"
              onClick={handleRotation}
              disabled={processing}
            >
              <div className="text-left">
                <div className="font-medium flex items-center gap-2">
                  🔄 Faire une rotation
                </div>
                <div className="text-xs text-primary-foreground/70 font-normal">
                  Libère les membres de leur chantier actuel pour les affecter ici
                </div>
              </div>
            </Button>

            {/* Option 2 : Ignorer le conflit */}
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-3 px-4"
              onClick={handleDemarrerMalgre}
              disabled={processing}
            >
              <div className="text-left">
                <div className="font-medium flex items-center gap-2">
                  ⚠️ Démarrer malgré le conflit
                </div>
                <div className="text-xs text-muted-foreground font-normal">
                  Les membres seront affectés sur les deux chantiers (double coût)
                </div>
              </div>
            </Button>

            {/* Option 3 : Modifier les dates */}
            <Button
              variant="ghost"
              className="w-full justify-start h-auto py-3 px-4"
              onClick={handleAnnulerConflit}
              disabled={processing}
            >
              <div className="text-left">
                <div className="font-medium flex items-center gap-2">
                  📅 Modifier les dates
                </div>
                <div className="text-xs text-muted-foreground font-normal">
                  Revenir en arrière pour changer les dates de démarrage
                </div>
              </div>
            </Button>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Annuler</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
